<template>
	<div class="nn-profile-form-shell">
		<form
			id="preferences-form"
			ref="preferencesForm"
			class="nn-profile-form"
			accept-charset="UTF-8"
			:aria-busy="loading"
			@submit.prevent
		>
			<input type="hidden" :name="csrfName" :value="csrfToken">
			<input type="hidden" name="action" value="users/save-user">
			<input type="hidden" name="userId" :value="currentUser.id">

			<div class="nn-profile-preference">
				<div class="nn-profile-preference__copy">
					<h3>Donkere modus</h3>
					<p id="dark-theme-description">Gebruik een donkere achtergrond, prettig bij weinig licht.</p>
				</div>
				<label class="nn-profile-switch">
					<input
						v-model="darkThemeEnabled"
						type="checkbox"
						name="fields[darkTheme]"
						aria-describedby="dark-theme-description"
						:disabled="loading"
						@change="saveThemePreference"
					>
					<span class="nn-profile-switch__track" aria-hidden="true"></span>
					<span class="nn-sr-only">Donkere modus</span>
				</label>
			</div>

			<div
				v-show="overlay"
				class="nn-profile-feedback"
				:class="{ 'is-error': error, 'is-success': success }"
				:role="error ? 'alert' : 'status'"
				:aria-live="error ? 'assertive' : 'polite'"
			>
				<loading-indicator v-if="loading"></loading-indicator>
				<span v-else-if="error" class="material-symbols-outlined" aria-hidden="true">error</span>
				<span v-else-if="success" class="material-symbols-outlined" aria-hidden="true">done</span>
				<p v-if="message" v-text="message"></p>
			</div>
		</form>
	</div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from './LoadingIndicator.vue'

export default {
	name: 'PreferencesForm',
	components: { LoadingIndicator },
	props: {
		currentUser: {
			type: Object,
			required: true,
		},
	},
	data() {
		const initialDarkTheme = Boolean(this.currentUser.darkTheme);

		return {
			overlay: false,
			error: false,
			success: false,
			loading: false,
			message: '',
			darkThemeEnabled: initialDarkTheme,
			savedDarkThemeEnabled: initialDarkTheme,
			themeChangeHandler: null,
		};
	},
	computed: {
		csrfName() {
			return window.csrfTokenName
		},
		csrfToken() {
			return window.csrfTokenValue
		},
	},
	mounted() {
		this.themeChangeHandler = (event) => {
			if (event.detail?.source === 'profile-preferences') {
				return;
			}

			if (typeof event.detail?.dark === 'boolean') {
				this.darkThemeEnabled = event.detail.dark;
				this.savedDarkThemeEnabled = event.detail.dark;
			}
		};
		window.addEventListener('nn:theme-change', this.themeChangeHandler);
	},
	beforeUnmount() {
		window.removeEventListener('nn:theme-change', this.themeChangeHandler);
	},
	methods: {
		applyTheme(enabled) {
			document.documentElement.classList.toggle('dark', enabled);
			document.body.classList.toggle('dark', enabled);
			window.dispatchEvent(new CustomEvent('nn:theme-change', {
				detail: { dark: enabled, source: 'profile-preferences' }
			}));
		},
		responseErrorMessage(data = {}) {
			const messages = [];

			if (data.error) {
				messages.push(data.error);
			}

			if (data.errors) {
				Object.values(data.errors).forEach((fieldErrors) => {
					if (Array.isArray(fieldErrors)) {
						messages.push(...fieldErrors);
					}
				});
			}

			return messages.join('\n');
		},
		showError(message, previousValue) {
			this.darkThemeEnabled = previousValue;
			this.savedDarkThemeEnabled = previousValue;
			this.applyTheme(previousValue);
			this.loading = false;
			this.error = true;
			this.success = false;
			this.message = message || 'Voorkeur opslaan mislukt. Probeer het opnieuw.';
			window.setTimeout(this.resetMessage, 5000);
		},
		saveThemePreference() {
			const previousValue = this.savedDarkThemeEnabled;
			const formData = new FormData(this.$refs.preferencesForm);
			formData.set('fields[darkTheme]', this.darkThemeEnabled ? '1' : '0');

			this.applyTheme(this.darkThemeEnabled);
			this.overlay = true;
			this.loading = true;
			this.error = false;
			this.success = false;
			this.message = '';

			axios({
				method: 'post',
				url: '/actions/users/save-user',
				responseType: 'json',
				data: formData,
				headers: {
					'Accept': 'application/json'
				}
			})
			.then((response) => {
				const errorMessage = this.responseErrorMessage(response.data);

				if (errorMessage) {
					this.showError(errorMessage, previousValue);
					return;
				}

				this.savedDarkThemeEnabled = this.darkThemeEnabled;
				this.loading = false;
				this.success = true;
				this.message = 'Voorkeur bijgewerkt.';
				window.setTimeout(this.resetMessage, 2200);
			})
			.catch((requestError) => {
				const responseData = requestError.response?.data || {};
				this.showError(this.responseErrorMessage(responseData) || responseData.message, previousValue);
			});
		},
		resetMessage() {
			this.message = '';
			this.error = false;
			this.success = false;
			this.overlay = false;
			this.loading = false;
		}
	},
};
</script>
